import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import CollapsibleText from '~/components/CollapsibleText/CollapsibleText';
import Counter from '~/components/Counter';
import Icon from '~/components/Icon/Icon';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '~/components/ModalComponents';
import { useProduct } from '~/components/ModalProductDetails/hooks/useProduct';
import { useCartVibration } from '~/hooks/useCartVibration';
import { useGlobal } from '~/hooks/useGlobal';
import { useNotifyMe } from '~/hooks/useNotifyMe';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { cartStore } from '~/stores';
import type { Offer, Product, ProductState } from '~/stores/CategoriesStore';
import { formatPriceWithCurrency } from '~/utils/formaters';
import { getLowestMultibuyLevel } from '~/utils/multibuy';

import BundleList from '../BundleList/BundleList';
import ProductFilters from '../ProductFilters/ProductFilters';
import ProductImagesColumn from '../ProductImagesColumn/ProductImagesColumn';

import styles from './ProductModal.module.scss';

interface ProductDataProps {
  productData: ProductState;
  onClose: () => void;
}

const ProductData = ({ productData, onClose }: ProductDataProps) => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const { data: product, selectedOffer } = productData as {
    data: Product;
    selectedOffer: Offer;
  };

  const {
    currentItem,
    propertyList,
    limit,
    oldPrice,
    currentPrice,
    isMultiBuy,
    isExpress,
    isBackorderAvailable,
    isBundle,
    isFavorite,
    deliveryToday,
    backorderDate,
    multiBuyInfo,
    selectOffer,
    toggleFavorite,
    handleCopyCode,
  } = useProduct(product as Product, selectedOffer as Offer);
  const { notifyMeOnClick, notifyMeIcon, notifyMeButtonText, btnClassName } =
    useNotifyMe(product.id || 0, selectedOffer?.sku || '', isBundle);

  const count = currentItem ? cartStore.getCartItemCountById(currentItem) : 0;

  const lowestLevel = currentItem
    ? getLowestMultibuyLevel(currentItem.multibuyPriceLevels)
    : null;

  const { vibrateOnChange } = useCartVibration({
    isMultibuy: isMultiBuy,
    requiredQuantity: lowestLevel?.quantity,
    currentCount: count,
  });

  const trackedProductIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (product && product.id !== trackedProductIdRef.current) {
      trackedProductIdRef.current = product.id;
      analyticsService.track(AnalyticsEvent.VIEW_ITEM, {
        currency: company.currency,
        value: product.discountPrice || product.price,
        items: [cartStore.convertToFirebase(product)],
      });
    }
  }, [product]);

  const bundleImages = product.images
    ? product.images
    : [`${product.previewImageThumb}`].filter(Boolean);

  const title = currentItem?.name;

  const handleChangeCount = (count: number, action: 'add' | 'remove') => {
    vibrateOnChange(count, action);

    if (currentItem && limit && count <= limit) {
      cartStore.setCartItemCountByProduct(
        {
          ...currentItem,
          ratingAverage: currentItem?.ratingAverage ?? 0,
          ratingMarksCount: currentItem?.ratingMarksCount ?? 0,
        },
        count,
        action,
        'product_main',
      );
    }
  };

  return (
    <>
      <ModalHeader
        className={styles.details__header}
        titleClassName={classes([
          styles.title_header,
          company.newDesignSystemEnabled && styles.newHeader,
        ])}
        title={title}
        onClose={onClose}
      />

      <ModalBody className={styles.details__body}>
        <div className={styles.details__body__container}>
          <ProductImagesColumn
            product={product}
            selectedOffer={selectedOffer}
            isExpress={isExpress}
            isBackorderAvailable={isBackorderAvailable}
            deliveryToday={deliveryToday}
            backorderDate={backorderDate}
            oldPrice={oldPrice}
            currentPrice={currentPrice}
            isFavorite={isFavorite}
            bundleImages={bundleImages}
            isBundle={isBundle}
            handleCopyCode={handleCopyCode}
            toggleFavorite={toggleFavorite}
          />
          <div className={styles.details__body__container__additional_info}>
            <ProductFilters
              selectedOffer={selectedOffer}
              isBundle={isBundle}
              productData={productData}
              selectOffer={selectOffer}
            />
            <BundleList isBundle={isBundle} offers={product.offers} />
            {currentItem?.description && (
              <CollapsibleText
                buttonContainerClassName={
                  styles.details__body__additional_info__description
                }
                text={currentItem.description}
              />
            )}
            {!isBundle && (
              <ul className={styles.characteristic_list}>
                {propertyList.map((prop) => (
                  <li
                    className={styles.characteristic_list_item}
                    key={prop.name}
                  >
                    <div className={styles.characteristic_list_item__name}>
                      <p>{prop.name}</p>
                    </div>
                    <div className={styles.characteristic_list_item__value}>
                      <p>{prop.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </ModalBody>

      <ModalFooter className={styles.details__footer}>
        <div
          className={styles.details__footer__price_container}
          data-company={company.name}
        >
          <div>
            <p
              className={classes([
                styles.details__footer__price_container__currentPrice,
                (oldPrice || isMultiBuy) &&
                  styles.details__footer__price_container__currentPrice_withDiscount,
              ])}
            >
              {currentPrice} / {t('item')}
            </p>
          </div>
          {oldPrice && (
            <p className={styles.details__footer__price_container__oldPrice}>
              {formatPriceWithCurrency(oldPrice)}
            </p>
          )}
        </div>
        {limit > 0 ? (
          <div className={styles.details__footer__price_container__counter}>
            <Counter
              count={count}
              containerClassName={styles.details__footer__counter}
              countClassName={styles.details__footer__counter__count}
              buttonClassName={styles.details__footer__counter__button}
              onClick={handleChangeCount}
              size={isMobile ? 'm' : 'l'}
              limit={limit || 99}
              disabled={!limit}
            />
            {!!(
              multiBuyInfo?.remainingCount && multiBuyInfo?.nextDiscountValue
            ) && (
              <span>
                {t('multibuyOfferWithCartValue', {
                  count: Math.max(0, multiBuyInfo.remainingCount),
                  value: multiBuyInfo.nextDiscountValue,
                })}
              </span>
            )}
          </div>
        ) : (
          <button
            className={classes([
              'button _bordered _m price-info-buttons__cta',
              btnClassName,
              styles.details__footer__notify_me_button,
            ])}
            onClick={notifyMeOnClick}
          >
            <Icon type={notifyMeIcon} size={24} />
            {notifyMeButtonText}
          </button>
        )}
      </ModalFooter>
    </>
  );
};

export default observer(ProductData);
